'use client'
import React, { useEffect, useRef, useState } from 'react'
import { TbAdjustmentsFilled } from 'react-icons/tb'
import { BsGlobeAsiaAustralia } from 'react-icons/bs'
import { FaBug, FaCog, FaRocket, FaUsers } from 'react-icons/fa'
import { HiMiniChartPie } from 'react-icons/hi2'
import { CustomFlowbiteTheme, Sidebar } from 'flowbite-react'
import { useSearchParams, usePathname } from 'next/navigation'
import { fetchEndSessionEndpoint, oidcSignOut } from '@/lib/auth/services'

export const customTheme: CustomFlowbiteTheme['sidebar'] = {
  root: {
    inner: 'h-full overflow-y-auto overflow-x-hidden bg-white py-4 px-3'
  },
  logo: {
    img: 'mr-3'
  }
}

const AppSidebar = () => {
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const isEvents = pathname.startsWith('/admin/events')
  const isBounty = pathname.startsWith('/admin/bug-bounty')
  const isUsers = pathname.startsWith('/admin/users')
  const status = searchParams.get('status') || 'published'

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [endSessionEndpoint, setEndSessionEndpoint] = useState<string>()
  const settingsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchEndSessionEndpoint().then((endpoint) => {
      if (endpoint) setEndSessionEndpoint(endpoint)
    })
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(e.target as Node)
      ) {
        setSettingsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Full OIDC sign-out: clears the shared `sso` cookie + IdP session, so the
  // user is logged out of the /web namespace too (not just the admin portal).
  const handleLogout = () => {
    setSettingsOpen(false)
    oidcSignOut(endSessionEndpoint)
  }

  return (
    <>
      <div
        className="fixed inset-y-0 z-50 flex w-72 flex-col">
        <Sidebar theme={customTheme}
          className="h-full w-72 overflow-y-auto bg-white">
          <div className="w-full flex h-16 items-center">
            <Sidebar.Logo className="mr-3 mb-0" href="#"
              img="/images/logo.svg" />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-gray-800">Hackathon</span>
              <span className="text-sm font-semibold text-gray-400">
                By AngelHack
              </span>
            </div>
          </div>

          <Sidebar.Items>
            <Sidebar.ItemGroup>
              <Sidebar.Item href="/admin/events" icon={HiMiniChartPie}>
                Overview
              </Sidebar.Item>
              <Sidebar.Collapse open={isEvents} icon={FaRocket} label="Hackathons">
                <Sidebar.Item className={`rounded-lg ${isEvents && status === 'published' && 'bg-gray-100'}`}
                  href="/admin/events?status=published">Published</Sidebar.Item>
                <Sidebar.Item className={`rounded-lg ${isEvents && status === 'draft' && 'bg-gray-100'}`}
                  href="/admin/events?status=draft">Drafts</Sidebar.Item>
              </Sidebar.Collapse>
              <Sidebar.Collapse open={isBounty} icon={FaBug} label="Bug Bounties">
                <Sidebar.Item className={`rounded-lg ${isBounty && status === 'published' && 'bg-gray-100'}`}
                  href="/admin/bug-bounty?status=published">Published</Sidebar.Item>
                <Sidebar.Item className={`rounded-lg ${isBounty && status === 'draft' && 'bg-gray-100'}`}
                  href="/admin/bug-bounty?status=draft">Drafts</Sidebar.Item>
              </Sidebar.Collapse>
              <Sidebar.Item href="/admin/users" icon={FaUsers}
                className={`rounded-lg ${isUsers && 'bg-gray-100'}`}>
                Users
              </Sidebar.Item>
            </Sidebar.ItemGroup>
          </Sidebar.Items>
        </Sidebar>

        <div
          className="w-full py-3 flex items-center justify-center gap-3 bg-white">
          <button
            className="inline-flex justify-center p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100">
            <TbAdjustmentsFilled size={24} className="rotate-180" />
          </button>
          <button
            className="inline-flex justify-center p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100">
            <BsGlobeAsiaAustralia size={24} className="rotate-180" />
          </button>
          <div className="relative" ref={settingsRef}>
            {settingsOpen && (
              <div className="absolute bottom-full left-1/2 mb-2 w-40 -translate-x-1/2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg z-50">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Log out
                </button>
              </div>
            )}
            <button
              type="button"
              aria-label="Settings"
              onClick={() => setSettingsOpen((v) => !v)}
              className="inline-flex justify-center p-2 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100">
              <FaCog size={24} className="rotate-180" />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
export default AppSidebar
